import React from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { Slider } from '@/components/ui/slider';

export interface GradientSettings {
  type: 'linear' | 'radial';
  angle: number;
  stops: { color: string; position: number }[];
}

interface Props {
  settings: GradientSettings;
  onChange: (settings: GradientSettings, cssString: string) => void;
}

export function generateGradientString(settings: GradientSettings): string {
  const sortedStops = [...settings.stops].sort((a, b) => a.position - b.position);
  const stopsString = sortedStops.map(s => `${s.color} ${s.position}%`).join(', ');
  if (settings.type === 'linear') {
    return `linear-gradient(${settings.angle}deg, ${stopsString})`;
  } else {
    return `radial-gradient(circle, ${stopsString})`;
  }
}

export const GradientEditor: React.FC<Props> = ({ settings, onChange }) => {
  const handleChange = (newSettings: Partial<GradientSettings>) => {
    const updated = { ...settings, ...newSettings };
    onChange(updated, generateGradientString(updated));
  };

  const updateStop = (index: number, updates: Partial<{ color: string; position: number }>) => {
    const newStops = [...settings.stops];
    newStops[index] = { ...newStops[index], ...updates };
    handleChange({ stops: newStops });
  };

  const removeStop = (index: number) => {
    if (settings.stops.length <= 2) return; // Need at least 2 stops
    const newStops = settings.stops.filter((_, i) => i !== index);
    handleChange({ stops: newStops });
  };

  const addStop = () => {
    if (settings.stops.length >= 8) return; // Limit to 8 stops
    const newStops = [...settings.stops];
    // Add a stop in the middle of the last two, or at 50%
    const last = newStops[newStops.length - 1];
    newStops.push({ color: last.color, position: Math.min(100, last.position + 10) });
    handleChange({ stops: newStops });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1.5">
          <Label className="text-[10px] uppercase text-muted-foreground">Type</Label>
          <Select 
            value={settings.type} 
            onValueChange={(v) => handleChange({ type: v as 'linear' | 'radial' })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="linear" className="text-xs">Linear</SelectItem>
              <SelectItem value="radial" className="text-xs">Radial</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {settings.type === 'linear' && (
          <div className="flex flex-col gap-1.5">
            <Label className="text-[10px] uppercase text-muted-foreground flex justify-between">
              <span>Angle</span>
              <span>{settings.angle}°</span>
            </Label>
            <div className="h-8 flex items-center">
              <Slider 
                value={[settings.angle]} 
                min={0} 
                max={360} 
                step={1}
                onValueChange={(v) => handleChange({ angle: v[0] })}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] uppercase text-muted-foreground">Colors</Label>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-5 px-1.5 text-[10px]" 
            onClick={addStop}
            disabled={settings.stops.length >= 8}
          >
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
        
        <div className="flex flex-col gap-2">
          {settings.stops.map((stop, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="color"
                value={stop.color}
                onChange={(e) => updateStop(i, { color: e.target.value })}
                className="w-6 h-6 rounded cursor-pointer border-0 p-0 shrink-0"
              />
              <div className="flex-1 flex items-center gap-2">
                <Slider 
                  value={[stop.position]} 
                  min={0} 
                  max={100} 
                  step={1}
                  onValueChange={(v) => updateStop(i, { position: v[0] })}
                  className="flex-1"
                />
                <span className="text-[10px] text-muted-foreground w-6 text-right shrink-0">{stop.position}%</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="w-6 h-6 shrink-0 text-muted-foreground hover:text-destructive"
                disabled={settings.stops.length <= 2}
                onClick={() => removeStop(i)}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
